import styles from './ProductBreakdown.module.scss';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import { headers, dataTypes } from 'constants/tableColumns';
import { BASE_URL } from 'constants/api';
import {
    selectSelectedRm,
    selectTimeslicerEndMonth,
    selectTimeslicerStartMonth
} from 'selectors/selectors';
import { resetAll } from 'slices/ScenarioFourSlice';

import Selecter from './Selecter/Selecter';
import DataRow from './DataRow/DataRow';

const ProductBreakdown = () => {
    const dispatch = useDispatch();

    const emptyColumn = { hasData: false, isFiltered: false };

    const selectedRm = useSelector(selectSelectedRm);
    const tsStart = useSelector(selectTimeslicerStartMonth) || {};
    const tsEnd = useSelector(selectTimeslicerEndMonth) || {};

    const initialColumns = [
        {
            hasData: true,
            isFiltered: false,
            field: 'rm',
            infoLabel: 'Risk Score'
        },
        {
            hasData: false,
            isFiltered: false,
            field: 'instrument_code'
        }
    ];

    const dropdownOptions = [
        { value: 'cif_id', label: headers.cifId, type: dataTypes.number },
        {
            value: 'instrument_type',
            label: headers.instrumentType,
            type: dataTypes.string
        },
        {
            value: 'instrument_subtype',
            label: headers.instrumentSubType,
            type: dataTypes.string
        },
        {
            value: 'instrument_code',
            label: headers.instrumentCode,
            type: dataTypes.string
        }
    ];

    const fieldData = [
        { field: 'rm', infoLabel: 'Risk Score', type: dataTypes.string },
        { field: 'cif_id', infoLabel: 'No.of Trades', type: dataTypes.number },
        {
            field: 'instrument_type',
            infoLabel: 'No.of Trades',
            type: dataTypes.string
        },
        {
            field: 'instrument_subtype',
            infoLabel: 'No.of Trades',
            type: dataTypes.string
        },
        {
            field: 'instrument_code',
            infoLabel: 'No.of Trades',
            type: dataTypes.string
        }
    ];

    const [columnList, setColumnList] = useState(initialColumns);
    const [filters, setFilters] = useState([]);
    const [headerCssClass, setHeaderCssClass] = useState('');
    const [selectedDataRow, setSelectedDataRow] = useState(null);

    useEffect(() => {
        return () => {
            dispatch(resetAll());
        };
    }, []);

    useEffect(() => {
        if (selectedRm) {
            setFilters([]);
            setColumnList([...initialColumns]);
            getDataForNextField('rm', selectedRm.label);
        }
    }, [selectedRm]);

    const handleScrollableStyles = (rows) => {
        if (headerCssClass === '' && rows.length > 4) {
            setHeaderCssClass(styles.scrollable);
        }
    };

    const getData = async (field, filters = [], colList) => {
        if (!field) throw Error('getData::Field params not found');

        // Check if column is already added with data
        const existingColumn = colList.find((f) => f.field === field);
        if (existingColumn && existingColumn.hasData) {
            return;
        }

        const url = `${BASE_URL}/scenario/four/client-product-breakdown`;
        const params = {
            start: tsStart.epoch,
            end: tsEnd.epoch
        };
        const payload = {
            filters: filters,
            selectField: field
        };

        const { data } = await axios.post(url, payload, { params });
        const breakdownData = data && data.clientProductBreakdown;

        return breakdownData;
    };

    const onValueChange = (field) => {
        let tempColList = [...columnList];
        const lastColumn = tempColList.pop();
        tempColList = [
            ...tempColList,
            {
                field: field,
                ...emptyColumn
            }
        ];

        setColumnList([...tempColList]);

        if (lastColumn.isMenuOpen) {
            const { field, dimensionValue } = selectedDataRow;
            getDataForNextField(field, dimensionValue, tempColList);
        }
    };

    const onValueClear = (field) => {
        const index = columnList.findIndex((c) => c.field === field);
        const prevColName = columnList[index - 1].field;
        const filterIndex = filters.findIndex(
            (f) => f.columnName === prevColName
        );
        let clearedColList = [...columnList];

        if (index > 0) {
            clearedColList[index - 1].isFiltered = false;
            clearedColList[index].hasData = false;
        }
        clearedColList = [
            ...clearedColList.slice(0, index),
            { ...emptyColumn }
        ];

        setColumnList([...clearedColList]);
        setFilters([...filters.splice(0, filterIndex)]);
    };

    const isPrevColFiltered = (field, filters) => {
        const index = columnList.findIndex((c) => c.field === field) - 1;

        const status =
            (index > -1 &&
                filters
                    .map((f) => f.columnName)
                    .includes(columnList[index].field)) ||
            !!selectedDataRow;

        return status;
    };

    const getDataForNextField = async (
        field,
        filterValue,
        colList = columnList
    ) => {
        const rowData = fieldData.find((f) => f.field === field);
        const currentFieldIndex = colList.findIndex((c) => c.field === field);
        const nextColumn = colList[currentFieldIndex + 1];
        const filter = filters.find((f) => f.columnName === field);
        const isSameColFilterValue =
            filter &&
            currentFieldIndex !== 0 &&
            filter.filterValue === filterValue;

        let tempColList = [...colList];
        let tempFilters = [...filters];

        // Go ahead only if next column dropdown is selected.
        if (!(nextColumn && nextColumn.field)) {
            return;
        }
        const nextColumnFieldName = nextColumn.field;

        if (isSameColFilterValue === false) {
            tempColList = [
                ...tempColList.splice(0, currentFieldIndex + 1),
                {
                    field: nextColumnFieldName,
                    ...emptyColumn
                }
            ];
            let index = tempFilters.findIndex((f) => f.columnName === field);
            tempFilters = tempFilters.splice(0, index);
        }

        tempColList[currentFieldIndex].isFiltered = true;

        /** Add or update filter values */
        const filterIndex = tempFilters.findIndex(
            (f) => f.columnName === field
        );
        if (filterIndex > -1) {
            tempFilters[filterIndex].value = filterValue;
        } else {
            tempFilters = [
                ...tempFilters,
                {
                    columnName: rowData.field,
                    filterValue: filterValue,
                    filterDataType: rowData.type
                }
            ];
        }

        setFilters(tempFilters);
        tempColList[currentFieldIndex].filterValue = filterValue;

        const breakdownData = await getData(
            nextColumnFieldName,
            tempFilters,
            tempColList
        );

        if (breakdownData) {
            const { total, productBreakdown } = breakdownData;
            const isPrevFiltered = isPrevColFiltered(
                nextColumnFieldName,
                tempFilters
            );

            tempColList.pop(); // Remove last empty column.

            tempColList = [
                ...tempColList,
                {
                    hasData: true,
                    isFiltered: isPrevFiltered,
                    field: nextColumnFieldName,
                    infoLabel: 'Risk Score',
                    total,
                    productBreakdown
                },
                isPrevFiltered && tempColList.length < 4
                    ? { ...emptyColumn }
                    : null
            ].filter((e) => !!e);

            handleScrollableStyles(breakdownData.productBreakdown);
        }
        setColumnList([...tempColList]);
        setSelectedDataRow(null);
    };

    const onDataRowClick = (data) => {
        const currentFieldIndex = columnList.findIndex(
            (c) => c.field === data.field
        );
        const nextColumn = columnList[currentFieldIndex + 1];
        const tempColList = [...columnList];

        /**
         * If next column is available, open dropdown menu
         */
        if (nextColumn) {
            tempColList[currentFieldIndex + 1] = {
                ...nextColumn,
                isMenuOpen: true
            };
        }

        if (!(nextColumn && nextColumn.field)) {
            setSelectedDataRow({
                field: data.field,
                dimensionValue: data.dimensionValue
            });
            setColumnList([...tempColList]);
            return;
        }

        getDataForNextField(data.field, data.dimensionValue);
    };

    const calculateMargin = (i) => {
        const stage = document.getElementById('cpb-stage');
        const colWidth = 158 * 5;
        let margin = 0;

        if (stage && i < 4) {
            const stageWidth = stage.offsetWidth;
            margin = (stageWidth - colWidth - 10) / 4;
        }
        return margin;
    };

    const getFilteredOptions = (dropdownOptions, colList) => {
        const fieldList = colList.map((c) => c.field).filter((c) => !!c);
        const filteredOptions = dropdownOptions.filter(
            (opt) => !fieldList.includes(opt.value)
        );
        return filteredOptions;
    };

    const getDataRowSelectedStatus = (column, rowData) => {
        const field = column.field;
        const filterValue = rowData.dimensionValue;
        const filter = filters.find((f) => f.columnName === field);

        if (!filter) return null;
        return filter.filterValue === filterValue;
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>Client: Product Breakdown</div>
            <div id="cpb-stage" className={styles.stage}>
                {selectedRm === null && (
                    <div className={styles.selectNotice}>
                        Select a RM from Concentrated EO Trades Chart
                    </div>
                )}

                {!!selectedRm && (
                    <>
                        <div
                            className={`${styles.filterHeaders} ${headerCssClass}`}
                        >
                            {columnList.map((col, i) => {
                                return i === 0 ? (
                                    <div
                                        key={i}
                                        className={styles.bdColumn}
                                        style={{
                                            marginRight: calculateMargin(i)
                                        }}
                                    >
                                        <div className={styles.ctoCol}>
                                            CTO Code
                                        </div>{' '}
                                        <div className={styles.colContent}>
                                            <div className={styles.value}>
                                                {selectedRm.label}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        key={i}
                                        className={styles.bdColumn}
                                        style={{
                                            marginRight: calculateMargin(i)
                                        }}
                                    >
                                        <div className={styles.filter}>
                                            <Selecter
                                                options={getFilteredOptions(
                                                    dropdownOptions,
                                                    columnList
                                                )}
                                                isMenuOpen={col.isMenuOpen}
                                                onValueChange={onValueChange}
                                                onValueClear={onValueClear}
                                                defaultValue={
                                                    dropdownOptions.find(
                                                        (opt) =>
                                                            opt.value ===
                                                            col.field
                                                    ) || null
                                                }
                                            />
                                        </div>
                                        {col.hasData && (
                                            <div className={styles.colContent}>
                                                <div className={styles.value}>
                                                    <span
                                                        className={styles.label}
                                                    >
                                                        {col.filterValue ||
                                                            `Total : ${col.total}`}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={styles.content}>
                            {columnList.map((col, i) => {
                                return i === 0 ? (
                                    <div
                                        key={i}
                                        className={styles.bdColumn}
                                        style={{
                                            marginRight: calculateMargin(i)
                                        }}
                                    >
                                        <div className={styles.colContent}>
                                            <DataRow
                                                percentage={100}
                                                header={selectedRm.label}
                                                infoLabel={'Risk Score'}
                                                infoValue={selectedRm.score}
                                                data={{
                                                    value: selectedRm.label,
                                                    field: 'rm'
                                                }}
                                                onDataRowClick={onDataRowClick}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        key={i}
                                        className={styles.bdColumn}
                                        style={{
                                            marginRight: calculateMargin(i)
                                        }}
                                    >
                                        {col.hasData && (
                                            <div className={styles.colContent}>
                                                <div className={styles.rowList}>
                                                    {(
                                                        col.productBreakdown ||
                                                        []
                                                    ).map((pbd, index) => (
                                                        <DataRow
                                                            key={index}
                                                            percentage={
                                                                pbd.percentage
                                                            }
                                                            header={
                                                                pbd.dimensionValue
                                                            }
                                                            infoValue={
                                                                pbd.tradesCount
                                                            }
                                                            data={col}
                                                            onDataRowClick={
                                                                onDataRowClick
                                                            }
                                                            isFilteredRow={getDataRowSelectedStatus(
                                                                col,
                                                                pbd
                                                            )}
                                                            svgWidth={
                                                                calculateMargin(
                                                                    i - 1
                                                                ) + 16
                                                            }
                                                            index={index}
                                                            prevColumn={
                                                                columnList[
                                                                    i - 1
                                                                ]
                                                            }
                                                            filters={filters}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ProductBreakdown;
